import { Box, Button, CircularProgress, Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CustomTabs from '../../components/common/CustomTabs'
import { Icons } from '../../utils/utilities'
import api from '../../service/api'
import CustomChip from '../../components/common/CustomChip'
import dayjs from 'dayjs'
import TableHeader from '../../components/common/TableHeader'
import noJobsImg from '../../assets/no-jobs.svg'
import { toast } from 'react-toastify'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'

const tabs = [
    { label: "Scheduled Jobs", value: "created", icon: Icons.default.Clock },
    { label: "Completed Jobs", value: "completed", icon: Icons.default.CheckCircleIcon },
    { label: "Failed Jobs", value: "failed", icon: Icons.default.ErrorIcon },
]

const dataTableColumns = [
    { name: "Sl. No.", minWidth: "100px", padding: "5px 20px" },
    { name: "Data name", minWidth: "150px", padding: "5px 20px" },
    { name: "Value", minWidth: "250px", padding: "5px 20px" },
]

const getFilterZeroItemMessage = ( filter ) => {
    switch ( filter ) {
        case "completed":
            return "No job has been completed yet!"

        case "created":
            return "No scheduled or active jobs found!"

        case "failed":
            return "No failed jobs to show!"

        default:
            return ""
    }
}

const JobBlock = ( { job, deleteJob } ) => {

    const [collapsed, setCollapsed] = useState( false )
    const [deleting, setDeleting] = useState( false )

    const { palette, border, layout } = useTheme()

    const initiateDeleteJob = async event => {
        setDeleting( true )
        event.stopPropagation()
        await deleteJob( job.id )
        setDeleting( false )
    }

    const jobStatusIconDetails = useMemo( () => {
        if ( job.state === "completed" )
            return { icon: Icons.default.CheckCircleIcon, color: "success" }
        if ( job.state === "failed" )
            return { icon: Icons.default.ErrorIcon, color: "error" }
        if ( job.state === "cancelled" )
            return { icon: Icons.default.CancelIcon, color: "greyed" }
        if ( job.state === "active" )
            return { icon: Icons.default.Pending, color: "success" }
        if ( job.state === "retry" )
            return { icon: Icons.default.Replay, color: "secondary" }
        if ( job.state === "created" )
            return { icon: Icons.default.Pending, color: "secondary" }

    }, [job] )

    return (
        <Box borderRadius={layout.borderRadius} border={border[1]} borderColor={collapsed ? palette.text.secondary : palette.borderColor} >
            <Box onClick={() => setCollapsed( prev => !prev )} bgcolor={collapsed ? palette.action.hover : ""} display="flex" justifyContent="space-between" alignItems="flex-end" sx={{ cursor: "pointer", ":hover": { bgcolor: palette.action.hover } }} padding="10px" >
                <Box>
                    <Typography fontSize="14px" textTransform="capitalize" variant='h2'>{job.name}-{job.id}</Typography>
                    <Box marginTop="5px" display="flex" gap="5px" alignItems="center" flexWrap="wrap">
                        <CustomChip tooltipTitle='Created At' startIcon={Icons.default.ViewListIcon} value={dayjs( job.created_on ).format( "LLL" )} />
                        <CustomChip tooltipTitle={!["active", "created"].includes( job.state ) ? 'Ran at' : 'Runs at'} startIcon={Icons.default.Clock} value={dayjs( job.start_after ).format( "LLL" )} />
                        <CustomChip tooltipTitle='Status' startIcon={jobStatusIconDetails.icon} color={jobStatusIconDetails.color} textStyles={{ textTransform: "capitalize" }} value={job.state} />
                    </Box>
                </Box>
                <Box>
                    {job.state === 'created' && <Tooltip disableInteractive placement='left' title="Delete this job">
                        <Button color='greyed' disabled={deleting} size='small' sx={{ color: palette.action.disabled, ":hover": { color: "error.main" } }} onClick={initiateDeleteJob} startIcon={deleting ? <CircularProgress size={14} /> : Icons.default.CancelIcon}>Remove Job</Button>
                    </Tooltip>}
                </Box>
            </Box>
            <Collapse in={collapsed}>
                <Box overflow="auto" borderTop={border[1]} display="flex" flexDirection="column" gap={layout.sectionsGap} alignItems="flex-start" padding="10px">
                    <Box maxWidth="100%">
                        <Typography gutterBottom variant='h6' fontSize="14px" component="h3" >Job data</Typography>
                        <TableContainer className='slim-custom-scrollbar' sx={{ border: border[1], borderRadius: layout.borderRadius }}  >
                            <Table>
                                <TableHeader columns={dataTableColumns} />
                                <TableBody>
                                    {Object.entries( job.data ).map( ( entry, idx ) => {
                                        return (
                                            <TableRow className='table-row' key={entry[0]}>
                                                <TableCell sx={{ padding: "5px 20px", fontSize: "12px" }} >{idx + 1}</TableCell>
                                                <TableCell sx={{ verticalAlign: "top", padding: "5px 20px", textTransform: "capitalize", fontSize: "12px" }} >{entry[0]}</TableCell>
                                                <TableCell sx={{ verticalAlign: "top", padding: "5px 20px", fontSize: "12px" }} >
                                                    <Typography fontSize="inherit" component="pre">
                                                        {typeof entry[1] !== 'string' ? JSON.stringify( entry[1] || {}, null, 2 ) : entry[1]}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    } )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    {job.output && <Box maxWidth="100%">
                        <Typography gutterBottom variant='h6' fontSize="14px" component="h3" >Output</Typography>
                        {typeof job.output !== 'string' && <TableContainer className='slim-custom-scrollbar' sx={{ border: border[1], borderRadius: layout.borderRadius }} >
                            <Table>
                                <TableHeader columns={dataTableColumns} />
                                <TableBody>
                                    {Object.entries( job.output ).map( ( entry, idx ) => {
                                        return (
                                            <TableRow className='table-row' key={entry[0]}>
                                                <TableCell sx={{ verticalAlign: "top", padding: "5px 20px", fontSize: "12px" }} >{idx + 1}</TableCell>
                                                <TableCell sx={{ verticalAlign: "top", padding: "5px 20px", textTransform: "capitalize", fontSize: "12px" }} >{entry[0]}</TableCell>
                                                <TableCell sx={{ verticalAlign: "top", padding: "5px 20px", fontSize: "12px" }} >
                                                    <Typography fontSize="inherit" component="pre">
                                                        {typeof entry[1] !== 'string' ? JSON.stringify( entry[1] || {}, null, 2 ) : entry[1]}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    } )}
                                </TableBody>
                            </Table>
                        </TableContainer>}

                        {typeof job.output === 'string' && <Typography component="pre">
                            {job.output}
                        </Typography>}
                    </Box>}
                </Box>
            </Collapse>
        </Box>
    )
}

const ScheduledWorkers = () => {

    const [selectedFilter, setSelectedFilter] = useState( 'created' )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )
    const [jobs, setJobs] = useState( [] )
    const [page, setPage] = useState( 1 )
    const [hasMore, setHasMore] = useState( false )
    const [loadingMore, setLoadingMore] = useState( false )

    const { palette, border, layout } = useTheme()

    const deleteAllJobs = async () => {
        try {
            await api.jobWorkers.deleteAttendanceJobs( { delete_type: "all" } )
            await fetchJobs()
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Failed to delete jobs." )
        }
    }

    const deleteJob = async jobId => {
        try {
            await api.jobWorkers.deleteAttendanceJobs( { delete_type: "id", job_ids: [jobId] } )
            setJobs( prev => {
                const newList = [...prev]
                return newList.filter( job => job.id !== jobId )
            } )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Failed to delete jobs." )
        }
    }

    const fetchJobs = useCallback(
        async ( pageNo = 1 ) => {
            pageNo === 1 ? setLoading( true ) : setLoadingMore( true )

            try {
                const { data } = await api.jobWorkers.fetchAttendanceJobs( { page: pageNo, state: selectedFilter } )

                if ( pageNo === 1 ) {
                    setJobs( data.data )
                } else {
                    setJobs( prev => [...prev, ...data.data] )
                }

                setHasMore( data.hasMore )
                setError( null )
            } catch ( err ) {
                setError( {
                    message: err?.response?.data?.message || "Something went wrong!",
                    code: err?.response?.status,
                } )
            } finally {
                setLoading( false )
                setLoadingMore( false )
            }
        },
        [selectedFilter]
    )

    useEffect( () => {
        setPage( 1 )
        fetchJobs( 1 )
    }, [fetchJobs] )

    const handleLoadMore = async () => {
        const nextPage = page + 1
        console.log( "Current page:", page )
        console.log( "Next page:", nextPage )
        setPage( nextPage )
        await fetchJobs( nextPage )
    }

    const filteredJobs = useMemo( () => {

        const filtered = jobs.filter( job => {
            if ( selectedFilter === 'completed' ) {
                return ["completed", "cancelled"].includes( job.state )
            } else if ( selectedFilter === 'failed' ) {
                return job.state === "failed"
            } else if ( selectedFilter === 'created' ) {
                return ["created", "retry", "active"].includes( job.state )
            }
            return false
        } )

        return filtered.sort(
            ( a, b ) =>
                new Date( b.createdOn ) - new Date( a.createdOn )
        )

    }, [selectedFilter, jobs] )

    // const displayedJobs = useMemo(() => {
    //     return jobs.slice(0, visibleCount)
    // }, [jobs, visibleCount])


    return (
        <Box flexGrow={1} padding={layout.pagePadding} overflow="auto" gap={layout.sectionsGap} display="flex" flexDirection="column">
            <Box display="flex" justifyContent="space-between" gap="20px" alignItems="center">
                <Box flexGrow="1">
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="14px">Job Workers</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Check jobs scheduled by alive servers.</Typography>
                </Box>
                <Box>
                    <Button
                        size="small"
                        color="secondary"
                        onClick={() => {
                            setPage( 1 )
                            fetchJobs( 1 )
                        }}
                    >
                        Refresh Jobs
                    </Button>
                    {/* <Button size='small' color='greyed' onClick={deleteAllJobs} >Delete all jobs</Button> */}
                </Box>
            </Box>

            {!error && <Box display="flex" flexDirection="column" gap={layout.cardsGap}>
                <CustomTabs variant="aligned-left" value={selectedFilter} handleTabChange={setSelectedFilter} tabs={tabs} />
                {filteredJobs.length > 0 &&
                    filteredJobs.map( ( job ) => (
                        <JobBlock
                            key={job.id}
                            deleteJob={deleteJob}
                            job={job}
                        />
                    ) )
                }


                {selectedFilter === "completed" && hasMore && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            disabled={loadingMore}
                            onClick={handleLoadMore}
                        >
                            {loadingMore ? "Loading..." : "Load More"}
                        </Button>
                    </Box>
                )}

                {!loading && !error && filteredJobs.length === 0 && (
                    <Box
                        alignItems="center"
                        padding="20px"
                        borderRadius={layout.borderRadius}
                        bgcolor={palette.contentBg}
                        display="flex"
                        flexDirection="column"
                        gap={layout.sectionsGap}
                    >
                        <Box
                            width="200px"
                            component="img"
                            src={noJobsImg}
                            alt="No jobs to show"
                        />
                        <Typography variant="subtitle2">
                            {getFilterZeroItemMessage( selectedFilter )}
                        </Typography>
                    </Box>
                )}
            </Box>}

            {!error && loading && <DataLoadingSpinner waitingMessage="Fecthing jobs, please wait..." />}

            {error && <TryAgainMessageBlock err={error?.message} getData={fetchJobs} code={error?.code} loading={loading} />}

        </Box>
    )
}

export default ScheduledWorkers
